import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildOrganizationDomainGroups,
  findOrganizationDomainKey,
  getOrganizationDomainLabel,
  organizationMatchesSearch,
} from "../utils/organizationDomains";
import { formatTitleCase } from "../utils/formatters";

export default function Header({
  organizations = [],
  selectedOrgId = "",
  onOrgChange,
  onOpenDeveloperDashboard,
  onOpenTeamDashboard,
  onConnectWhatsApp,
  onDisconnectWhatsApp,
  onLogout,
  userDisplayName = "",
  userEmail = "",
  userRole = "user",
  canManageEmployees = false,
  whatsappConnection = null,
  isWhatsAppConnecting = false,
  isWhatsAppDisconnecting = false,
}) {
  const identityLabel = String(userDisplayName || "").trim() || String(userEmail || "").trim();
  const normalizedRole = String(userRole || "").trim().toLowerCase();
  const isAdminUser = normalizedRole === "admin";
  const isManagerUser =
    canManageEmployees || normalizedRole === "client_manager" || normalizedRole === "manager";
  const profileRoleClass = isAdminUser ? "admin" : isManagerUser ? "manager" : "employee";
  const profileRoleLabel = isAdminUser ? "Admin" : isManagerUser ? "Manager" : "Employee";
  const profileRoleMark = isAdminUser ? "A" : isManagerUser ? "M" : "E";
  const profileInitial = String(identityLabel || "Profile").trim().charAt(0).toUpperCase() || "P";
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
  const [orgSearchText, setOrgSearchText] = useState("");
  const [selectedDomainKey, setSelectedDomainKey] = useState("");
  const profileMenuRef = useRef(null);
  const orgMenuRef = useRef(null);
  const previousSelectedOrgIdRef = useRef("");
  const selectedOrg = organizations.find((org) => String(org.org_id) === String(selectedOrgId)) || null;
  const domainGroups = useMemo(() => buildOrganizationDomainGroups(organizations), [organizations]);
  const selectedDomainExists = domainGroups.some((group) => group.key === selectedDomainKey);
  const activeDomainKey =
    (selectedDomainExists ? selectedDomainKey : "") ||
    findOrganizationDomainKey(organizations, selectedOrgId) ||
    domainGroups[0]?.key ||
    "";
  const activeDomain = domainGroups.find((group) => group.key === activeDomainKey) || domainGroups[0] || null;
  const filteredOrganizations = (activeDomain?.organizations || []).filter((org) =>
    organizationMatchesSearch(org, orgSearchText),
  );
  const isWhatsAppConnected = Boolean(whatsappConnection?.connected);
  const whatsappWabaId = String(whatsappConnection?.waba_id || "").trim();
  const whatsappDisplayPhone = String(
    whatsappConnection?.display_phone_number || whatsappConnection?.phone_number || "",
  ).trim();
  const whatsappPhoneNumberId = String(whatsappConnection?.phone_number_id || "").trim();
  const whatsappDetails = [
    { label: "WABA", value: whatsappWabaId },
    {
      label: whatsappDisplayPhone ? "Phone" : "Phone ID",
      value: whatsappDisplayPhone || whatsappPhoneNumberId,
    },
  ].filter((item) => item.value);
  const whatsappDetailText = whatsappDetails.length
    ? whatsappDetails.map((item) => `${item.label} ${item.value}`).join(" | ")
    : "Account details loading";
  const canDisconnectWhatsApp =
    isWhatsAppConnected && typeof onDisconnectWhatsApp === "function" && !isWhatsAppConnecting;
  const isWhatsAppBusy = isWhatsAppConnecting || isWhatsAppDisconnecting;
  const whatsappButtonLabel = isWhatsAppConnecting
    ? "Connecting..."
    : isWhatsAppDisconnecting
      ? "Disconnecting..."
      : isWhatsAppConnected
        ? "WhatsApp Connected"
        : "Connect WhatsApp";
  const whatsappButtonTitle = isWhatsAppConnected
    ? `WhatsApp is connected for this workspace. ${whatsappDetailText}`
    : "Connect WhatsApp for this workspace";

  useEffect(() => {
    const previousSelectedOrgId = previousSelectedOrgIdRef.current;
    previousSelectedOrgIdRef.current = String(selectedOrgId || "");

    const nextDomainKey = findOrganizationDomainKey(organizations, selectedOrgId);
    if (String(previousSelectedOrgId || "") !== String(selectedOrgId || "") && nextDomainKey) {
      setSelectedDomainKey(nextDomainKey);
    } else if (selectedDomainKey && !selectedDomainExists) {
      setSelectedDomainKey(domainGroups[0]?.key || "");
    } else if (!selectedDomainKey && domainGroups[0]?.key) {
      setSelectedDomainKey(domainGroups[0].key);
    }
  }, [domainGroups, organizations, selectedDomainExists, selectedDomainKey, selectedOrgId]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.body.classList.remove("app-theme-dark");
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("rfm_theme");
    }
  }, []);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
      if (orgMenuRef.current && !orgMenuRef.current.contains(event.target)) {
        setIsOrgMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
    };
  }, []);

  return (
    <header className="app-header">
      <div className="logo-wrap">
        <img className="logo-mark" src="/rfm-logo.png" alt="" aria-hidden="true" />
        <span className="logo-text">KorvRFM</span>
      </div>

      <div className="header-right-zone">
        <div className="header-org-filter" ref={orgMenuRef}>
          <label className="header-org-label" htmlFor="org-filter-search">Customer data workspace</label>
          <div className="header-org-control">
            <span className="header-org-icon" aria-hidden="true">
              <svg viewBox="0 0 20 20">
                <path d="M3.5 5.5h13" />
                <path d="M5 3.5h10a1.5 1.5 0 0 1 1.5 1.5v10A1.5 1.5 0 0 1 15 16.5H5A1.5 1.5 0 0 1 3.5 15V5A1.5 1.5 0 0 1 5 3.5z" />
                <path d="M7 9h6" />
                <path d="M7 12.5h4" />
              </svg>
            </span>
            <div className="header-org-copy">
              <span>Customer data view</span>
              <button
                type="button"
                className="header-org-trigger"
                onClick={() => {
                  setIsOrgMenuOpen((current) => !current);
                  setOrgSearchText("");
                  setSelectedDomainKey(findOrganizationDomainKey(organizations, selectedOrgId) || domainGroups[0]?.key || "");
                }}
                aria-haspopup="listbox"
                aria-expanded={isOrgMenuOpen}
              >
                <strong>{selectedOrg ? formatTitleCase(selectedOrg.name || selectedOrg.org_name) : "No workspace found"}</strong>
                {selectedOrg ? <small>{getOrganizationDomainLabel(selectedOrg)}</small> : null}
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="m6 8 4 4 4-4" />
                </svg>
              </button>
            </div>
          </div>
          {isOrgMenuOpen ? (
            <div className="header-org-menu" role="listbox" aria-label="Customer data workspaces">
              <div className="header-org-search">
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <circle cx="9" cy="9" r="5" />
                  <path d="m13 13 4 4" />
                </svg>
                <input
                  id="org-filter-search"
                  type="search"
                  value={orgSearchText}
                  onChange={(event) => setOrgSearchText(event.target.value)}
                  placeholder="Search organization"
                  autoFocus
                />
              </div>
              <div className="header-domain-tabs" role="tablist" aria-label="Workspace sectors">
                {domainGroups.map((group) => (
                  <button
                    key={group.key}
                    type="button"
                    className={`header-domain-tab ${group.key === activeDomainKey ? "active" : ""}`.trim()}
                    onClick={() => {
                      setSelectedDomainKey(group.key);
                      setOrgSearchText("");
                    }}
                    role="tab"
                    aria-selected={group.key === activeDomainKey}
                  >
                    <span>{group.label}</span>
                    <strong>{group.organizations.length}</strong>
                  </button>
                ))}
              </div>
              <div className="header-org-options premium-scroll">
                {activeDomain ? (
                  <div className="header-org-group-label">
                    <span>{activeDomain.label}</span>
                    <strong>{filteredOrganizations.length}</strong>
                  </div>
                ) : null}
                {filteredOrganizations.map((org) => {
                  const isSelected = String(org.org_id) === String(selectedOrgId);
                  return (
                    <button
                      key={org.org_id}
                      type="button"
                      className={`header-org-option ${isSelected ? "selected" : ""}`.trim()}
                      onClick={() => {
                        onOrgChange?.(org.org_id);
                        setIsOrgMenuOpen(false);
                        setOrgSearchText("");
                      }}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <span className="header-org-option-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20">
                          <path d="M4 16.5V5.2l6-2.4 6 2.4v11.3" />
                          <path d="M2.8 16.5h14.4" />
                          <path d="M7 7h.1" />
                          <path d="M10 7h.1" />
                          <path d="M13 7h.1" />
                          <path d="M8.7 16.5v-3.2h2.6v3.2" />
                        </svg>
                      </span>
                      <span>
                        <strong>{formatTitleCase(org.name || org.org_name)}</strong>
                        <small>{getOrganizationDomainLabel(org)}</small>
                      </span>
                    </button>
                  );
                })}
                {!organizations.length ? <p className="header-org-empty">No workspace found.</p> : null}
                {organizations.length && !filteredOrganizations.length ? (
                  <p className="header-org-empty">No organization matches your search.</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="header-profile-wrap" ref={profileMenuRef}>
          {onConnectWhatsApp ? (
            <div className={`header-whatsapp-cluster ${isWhatsAppConnected ? "connected" : ""}`.trim()}>
              <button
                type="button"
                className={`header-whatsapp-btn ${isWhatsAppConnected ? "connected" : ""}`.trim()}
                onClick={onConnectWhatsApp}
                disabled={isWhatsAppBusy}
                title={whatsappButtonTitle}
              >
                <span className="header-whatsapp-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20">
                    <path d="M10 3.2a6.8 6.8 0 0 0-5.8 10.3L3.5 17l3.6-.7A6.8 6.8 0 1 0 10 3.2z" />
                    <path d="M7.4 7.1c.2-.4.4-.4.6-.4h.5c.2 0 .4.1.5.4l.5 1.2c.1.2 0 .4-.1.5l-.3.4c-.1.1-.2.3 0 .5.4.7 1 1.3 1.7 1.7.2.1.4.1.5 0l.5-.4c.1-.1.3-.2.5-.1l1.2.6c.3.1.4.3.4.5v.4c0 .3-.2.6-.5.8-.5.3-1.3.4-2.4 0-1.7-.6-3.2-2-4-3.6-.5-1-.6-1.9-.2-2.5z" />
                  </svg>
                </span>
                <span className="header-whatsapp-copy">
                  <strong>{whatsappButtonLabel}</strong>
                  {isWhatsAppConnected ? (
                    <span className="header-whatsapp-detail-list">
                      {whatsappDetails.length ? (
                        whatsappDetails.map((item) => (
                          <small key={item.label}>
                            <span>{item.label}</span>
                            <b>{item.value}</b>
                          </small>
                        ))
                      ) : (
                        <small>
                          <span>Status</span>
                          <b>Account details loading</b>
                        </small>
                      )}
                    </span>
                  ) : null}
                </span>
              </button>
              {canDisconnectWhatsApp ? (
                <button
                  type="button"
                  className="header-whatsapp-disconnect-btn"
                  onClick={onDisconnectWhatsApp}
                  disabled={isWhatsAppDisconnecting}
                  title="Disconnect WhatsApp for this workspace"
                >
                  Disconnect
                </button>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="header-profile-btn"
            onClick={() => setIsProfileMenuOpen((current) => !current)}
            aria-haspopup="menu"
            aria-expanded={isProfileMenuOpen}
          >
            <span className={`header-profile-avatar ${profileRoleClass}`} aria-label={`${profileRoleLabel} profile`}>
              <span className="header-profile-avatar-initial">{profileInitial}</span>
              <span className="header-profile-avatar-role-mark">{profileRoleMark}</span>
            </span>
            <span className="header-profile-copy">
              <strong>{identityLabel || "Profile"}</strong>
              <small>Preferences</small>
            </span>
            <span className="header-profile-chevron" aria-hidden="true">▾</span>
          </button>

          {isProfileMenuOpen ? (
            <div className="header-profile-menu" role="menu" aria-label="Profile actions">
              {onOpenDeveloperDashboard ? (
                <button
                  type="button"
                  className="header-profile-item"
                  onClick={() => {
                    setIsProfileMenuOpen(false);
                    onOpenDeveloperDashboard?.();
                  }}
                  role="menuitem"
                >
                  <span className="header-profile-item-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20">
                      <path d="M4 4h12v12H4z" />
                      <path d="M4 8h12" />
                      <path d="M8 4v12" />
                    </svg>
                  </span>
                  <span>Admin Dashboard</span>
                </button>
              ) : null}

              {onOpenTeamDashboard ? (
                <button
                  type="button"
                  className="header-profile-item"
                  onClick={() => {
                    setIsProfileMenuOpen(false);
                    onOpenTeamDashboard?.();
                  }}
                  role="menuitem"
                >
                  <span className="header-profile-item-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20">
                      <path d="M6 8a2.3 2.3 0 1 1 0-4.6A2.3 2.3 0 0 1 6 8z" />
                      <path d="M14 8a2.3 2.3 0 1 1 0-4.6A2.3 2.3 0 0 1 14 8z" />
                      <path d="M2.7 16c0-2.2 1.6-3.8 3.8-3.8H9" />
                      <path d="M11 16c0-2.2 1.6-3.8 3.8-3.8h2.5" />
                    </svg>
                  </span>
                  <span>Team Dashboard</span>
                </button>
              ) : null}

              <button
                type="button"
                className="header-profile-item danger"
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  onLogout?.();
                }}
                role="menuitem"
              >
                <span className="header-profile-item-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20">
                    <path d="M8 3h-3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3" />
                    <path d="M13 6l4 4-4 4" />
                    <path d="M17 10H8" />
                  </svg>
                </span>
                <span>Logout</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
